// src/utils/quoteSchema.js
// PHASE 1A VERIFIER: r1-p1a-clientschema-8b3d7f29
//
// Client-side (ESM) mirror of functions/utils/quoteSchema.js.
//
// The server module is CommonJS and lives inside the Cloud Functions
// package, so the React app can't import from it directly. This file
// duplicates the small set of constants and helpers that the client
// actually needs.
//
// KEEP IN SYNC with functions/utils/quoteSchema.js. The constants here
// are intentionally a SUBSET of the server module — only what the web
// app needs. If you add a new SELECTION_TYPES value on the server,
// add it here too.

// ────────────────────────────────────────────────────────────────────
// Line item selection types
// ────────────────────────────────────────────────────────────────────

export const SELECTION_TYPES = {
  REQUIRED:    'required',
  RECOMMENDED: 'recommended',
  ADDON:       'addon',
  INCLUDED:    'included',
};

export const SELECTION_TYPE_VALUES = Object.values(SELECTION_TYPES);

export const DEFAULT_SELECTION_TYPE = SELECTION_TYPES.REQUIRED;

export function isValidSelectionType(value) {
  return SELECTION_TYPE_VALUES.includes(value);
}

// ────────────────────────────────────────────────────────────────────
// Quote statuses
// ────────────────────────────────────────────────────────────────────
//
// Canonical going forward: PascalCase. Legacy data may contain
// "Accepted" or lowercase variants; the predicates below tolerate both.

export const QUOTE_STATUSES = {
  DRAFT:             'Draft',
  SENT:              'Sent',
  // Signed off by the customer but a REQUIRED deposit is not yet paid.
  // Holds the estimate out of Approved until the deposit-paid webhook fires.
  AWAITING_DEPOSIT:  'AwaitingDeposit',
  APPROVED:          'Approved',
  DECLINED:          'Declined',
  CHANGES_REQUESTED: 'ChangesRequested',
  EXPIRED:           'Expired',
};

// "Approved" semantically — includes the legacy "Accepted" value.
// Use this predicate everywhere instead of hardcoding string matches.
export function isApprovedStatus(status) {
  if (!status || typeof status !== 'string') return false;
  const s = status.toLowerCase();
  return s === 'approved' || s === 'accepted';
}

export function isDeclinedStatus(status) {
  if (!status || typeof status !== 'string') return false;
  return status.toLowerCase() === 'declined';
}

// Tolerant matcher for the new 'ChangesRequested' status. Accepts
// 'ChangesRequested', 'Changes Requested', 'changes_requested', etc.
export function isChangesRequestedStatus(status) {
  if (!status || typeof status !== 'string') return false;
  const s = status.toLowerCase().replace(/[_\s-]/g, '');
  return s === 'changesrequested';
}

// Map any known status variant to the canonical PascalCase form.
// Returns the original string for unknown values.
export function normalizeStatus(status) {
  if (!status || typeof status !== 'string') return status;
  const s = status.toLowerCase().replace(/[_\s-]/g, '');
  if (s === 'accepted' || s === 'approved')         return QUOTE_STATUSES.APPROVED;
  if (s === 'declined')                              return QUOTE_STATUSES.DECLINED;
  if (s === 'changesrequested')                      return QUOTE_STATUSES.CHANGES_REQUESTED;
  if (s === 'draft')                                 return QUOTE_STATUSES.DRAFT;
  if (s === 'sent')                                  return QUOTE_STATUSES.SENT;
  if (s === 'awaitingdeposit')                       return QUOTE_STATUSES.AWAITING_DEPOSIT;
  if (s === 'expired')                               return QUOTE_STATUSES.EXPIRED;
  return status;
}

// ────────────────────────────────────────────────────────────────────
// itemId generator
// ────────────────────────────────────────────────────────────────────
//
// PHASE 3 VERIFIER: r1-p3-clientschema-genid-4f7b8c92
//
// Stable UUID assigned at line-item creation time. Once assigned,
// itemId NEVER changes — it persists through edits, reorders, template
// re-applications, and quote-to-job/invoice conversions. The audit
// trail anchors on this value.
//
// Uses crypto.randomUUID() when available (all modern browsers + Node
// 14.17+). Falls back to a v4-compatible polyfill if not.
//
// KEEP IN SYNC with functions/utils/quoteSchema.js — same helper
// lives there for server-side use.
export function generateItemId() {
  if (typeof crypto !== 'undefined' && crypto && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// ────────────────────────────────────────────────────────────────────
// Document terminology (Quote vs Estimate)
// ────────────────────────────────────────────────────────────────────
//
// PHASE 1 VERIFIER: r1-p1-docterm-clientschema-9c4e1a7b
//
// Presentation-only. Each document is stamped with a docType
// ('quote' | 'estimate') at creation and it is LOCKED thereafter; the
// Q-/E- prefix is baked into quoteNumber at that moment and never
// reformatted. Only the displayed *word* ever varies.
//
// resolveDocTerminology maps a docType to its display words + prefix.
// The freeze rule lives entirely in the fallback: any docType that is
// not exactly 'estimate' — including absent (every legacy quote), null,
// or '' — resolves to Quote / 'Q'. The match is strict, not
// case-insensitive: we only ever store the controlled lowercase
// DOC_TYPES values.
//
// The helper is source-agnostic — it knows nothing about org defaults.
// Per-document surfaces call resolveDocTerminology(quote.docType);
// aggregate surfaces call resolveDocTerminology(orgDefault). Selecting
// the source is the caller's job.
//
// KEEP IN SYNC with functions/utils/quoteSchema.js (server twin, added
// in Phase 2) and the mobile util (Phase 4).

export const DOC_TYPES = { QUOTE: 'quote', ESTIMATE: 'estimate' };

export function resolveDocTerminology(docType) {
  return docType === 'estimate'
    ? { type: 'estimate', word: 'Estimate', wordPlural: 'Estimates', prefix: 'E' }
    : { type: 'quote',    word: 'Quote',    wordPlural: 'Quotes',    prefix: 'Q' };
}

// ====================================================================
// Deposits (Deposits Roadmap V1 — Seam 1)
// SEAM 1 VERIFIER: r1-dep-clischema-7f3a9d42
// ====================================================================
//
// Client ESM mirror of the deposit constants and money math in
// functions/utils/quoteSchema.js. KEEP IN SYNC — same rule as the
// rest of this file.
//
// Trust split: deposit.type and deposit.value are owner-editable
// config (pre-approval only, enforced by firestore.rules).
// depositStatus and all other deposit.* fields are Cloud-Function-only.
// The builder (Seam 6) uses DEPOSIT_TYPES + resolveDepositAmount for
// its live preview; badge composition uses DEPOSIT_STATUSES.
//
// SEAM 4 VERIFIER: r2-dep-carrythrough-5b8d2e91 — read-only on the client:
//   deposit.appliedToInvoiceId — invoice that captured this deposit; its
//       presence means "already spent" (the idempotency lock).
//   deposit.appliedAmount      — amount that actually landed there.
//   deposit.appliedAt          — when.
//   deposit.unappliedRemainder — leftover when the deposit exceeded the
//       invoice total. Frozen by design; the owner applies it manually.
// All four are written only by the applyDepositToInvoice trigger. See the
// full rationale in functions/utils/quoteSchema.js.

export const DEPOSIT_TYPES = {
  PERCENTAGE: 'percentage',
  FIXED:      'fixed',
};

export const DEPOSIT_TYPE_VALUES = Object.values(DEPOSIT_TYPES);

export function isValidDepositType(value) {
  return DEPOSIT_TYPE_VALUES.includes(value);
}

export const DEPOSIT_STATUSES = {
  UNPAID:   'unpaid',
  PAID:     'paid',
  RECORDED: 'recorded',
  REFUNDED: 'refunded',
  WAIVED:   'waived',
};

export const DEPOSIT_STATUS_VALUES = Object.values(DEPOSIT_STATUSES);

export function isValidDepositStatus(value) {
  return DEPOSIT_STATUS_VALUES.includes(value);
}

// Interac e-Transfer is a named first-class option (Canadian market).
export const DEPOSIT_OFFLINE_METHODS = {
  ETRANSFER: 'etransfer',
  CASH:      'cash',
  CHEQUE:    'cheque',
  OTHER:     'other',
};

export const DEPOSIT_OFFLINE_METHOD_VALUES = Object.values(DEPOSIT_OFFLINE_METHODS);

export const DEFAULT_DEPOSIT_REMINDER_DAYS = [2, 5];

// Mirror of the server's resolveDepositAmount — Decision 3 money math.
// percentage of APPROVED total; fixed capped at approved total; cents.
export function resolveDepositAmount(type, value, approvedTotal) {
  const total = Number(approvedTotal);
  const val   = Number(value);
  if (!Number.isFinite(total) || total <= 0) return 0;
  if (!Number.isFinite(val)   || val   <= 0) return 0;

  let amount;
  if (type === DEPOSIT_TYPES.PERCENTAGE) {
    amount = total * (val / 100);
  } else if (type === DEPOSIT_TYPES.FIXED) {
    amount = val;
  } else {
    return 0;
  }

  if (amount > total) amount = total;
  return Math.round(amount * 100) / 100;
}

// Hybrid approval model (Deposits Roadmap V2 — added post-July-13, after
// the original Seam 1 constants above): whether a paid deposit GATES
// estimate approval. Two levels, mirroring the quoteTerminologyDefault
// pattern —
//   - org doc:      depositRequiredDefault (bool) — org-wide default
//   - per-estimate: depositRequired (bool) — overrides the org default
// Existing orgs/estimates are never backfilled; both read through a
// code-side fallback to DEFAULT_DEPOSIT_REQUIRED (deposits gate nothing
// until an org opts in via Settings in a later seam).
// KEEP IN SYNC with functions/utils/quoteSchema.js.
export const DEFAULT_DEPOSIT_REQUIRED = false;

// Resolve the effective "deposit required" flag for one estimate.
// Uses ?? (NOT ||) so an explicit per-estimate `false` correctly
// overrides an org default of `true` — with || a false override would be
// swallowed. Coerces the result to a real boolean.
export function resolveDepositRequired(perEstimateValue, orgDefaultValue) {
  const v = perEstimateValue ?? orgDefaultValue ?? DEFAULT_DEPOSIT_REQUIRED;
  return v === true;
}

// depdraft-3c8e5a14 — MIRROR of functions/utils/quoteSchema.js (same words, same rule). A Draft may be saved
// with "Collect a deposit" ON and no amount: stored as deposit.value 0 + depositAmountNeeded: true. The marker
// counts only while it is true, the amount is not above zero, there is no depositStatus and the status is
// Draft or Sent; otherwise it is stale and ignored. sendQuoteEmail refuses such an estimate with this sentence.
export const DEPOSIT_AMOUNT_NEEDED_MESSAGE = 'Enter the deposit amount before sending.';

export function isDepositAmountNeeded(quote) {
  if (!quote || quote.depositAmountNeeded !== true) return false;
  if (quote.depositStatus) return false;
  const value = Number(quote.deposit && quote.deposit.value);
  if (Number.isFinite(value) && value > 0) return false;
  const status = normalizeStatus(quote.status);
  return status === QUOTE_STATUSES.DRAFT || status === QUOTE_STATUSES.SENT;
}

// ────────────────────────────────────────────────────────────────────
// Quote provenance / ownership (Seam 0)
// ────────────────────────────────────────────────────────────────────

// Write-side, create-time salesperson attribution for a NEW quote. Reads the
// customer's rep off the CUSTOMER document (snake_case sales_person_id is
// canonical THERE — never rename or rewrite it), and the result is written as
// camelCase salesPersonId on the QUOTE. Falls back to the creating user, then
// null. The request/lead's assignedTo is deliberately NOT an input: a quote
// created from a lead attributes to the customer's rep, not the lead's assignee.
export function resolveCreateSalesPersonId(customer, creatingUid) {
  return customer?.sales_person_id || creatingUid || null;
}

// Read-side salesperson resolution for an existing quote — the existing inline
// precedence, extracted.
// Intentionally unused on this surface. Present for three-way parity with
// functions/utils/quoteSchema.js — do not remove.
export function resolveQuoteSalesPerson(quote) {
  return quote?.salesPersonId || quote?.sales_person_id || quote?.createdBy || null;
}

// Org-level default deposit AMOUNT (org settings seam). Shape mirrors the
// per-estimate deposit object — same allowed types, same units (percentage
// stored as 25, not 0.25) — plus an explicit `enabled` flag the per-estimate
// object doesn't have. On an estimate, "no deposit" is expressed as value 0;
// here we keep the flag separate so an admin can switch the default off
// without losing the number they configured.
//
//   org doc: depositDefaults { enabled: bool, type: 'percentage'|'fixed', value: number }
//
// Read only by the estimate builder to seed a BRAND-NEW estimate. A saved
// per-estimate config always wins. DELIBERATELY NOT MIRRORED into
// functions/utils/quoteSchema.js: the server never resolves this — it reads
// the per-estimate deposit object the builder already stamped — so a copy
// there would be a dead export that forces a functions deploy for nothing.
export const DEFAULT_DEPOSIT_DEFAULTS = {
  enabled: false,
  type:    DEPOSIT_TYPES.PERCENTAGE,
  value:   0,
};

// Normalize whatever is on the org doc into a safe { enabled, type, value }.
// Never throws, never returns a partial: an org that has never opened the
// settings screen resolves to DEFAULT_DEPOSIT_DEFAULTS.
export function resolveDepositDefaults(orgDefaults) {
  const d   = orgDefaults || {};
  const val = Number(d.value);
  return {
    enabled: d.enabled === true,
    type:    isValidDepositType(d.type) ? d.type : DEFAULT_DEPOSIT_DEFAULTS.type,
    value:   Number.isFinite(val) && val > 0 ? val : 0,
  };
}

// ────────────────────────────────────────────────────────────────────
// Estimate validity period (Seam 2 — send & expiry hygiene) qvalid-7c2e4a91
// ────────────────────────────────────────────────────────────────────
//   org doc: quoteDefaults { validityDays: number }
//
// How many days a NEW estimate stays valid (expiresAt = creation day + N).
// Read at CREATE time only — existing estimates keep the expiresAt they were
// saved with. The fallback is 30, the old hardcoded value, so an org that
// never opens the setting sees no change. Capped at 30 because the emailed
// portal login link also dies at 30 days: a longer validity would outlive
// the customer's link (longer periods are DEFERRED in BACKLOG).
// Three-way mirror: web, functions/utils/quoteSchema.js, mobile.
export const DEFAULT_QUOTE_VALIDITY_DAYS = 30;
export const MAX_QUOTE_VALIDITY_DAYS     = 30;
export const QUOTE_VALIDITY_PRESETS      = [7, 10, 14, 21, 30];

// Never throws: anything that isn't a whole number of days in 1..MAX
// resolves to DEFAULT_QUOTE_VALIDITY_DAYS.
export function resolveQuoteValidityDays(quoteDefaults) {
  const n = Number(quoteDefaults?.validityDays);
  return Number.isInteger(n) && n >= 1 && n <= MAX_QUOTE_VALIDITY_DAYS ? n : DEFAULT_QUOTE_VALIDITY_DAYS;
}

// ────────────────────────────────────────────────────────────────────
// Derived expiry state (Seam 5 — expiry activation) qexp-6f2a9d38
// ────────────────────────────────────────────────────────────────────
// VERBATIM mirror of the BEGIN..END block in functions/utils/quoteSchema.js
// (this copy only adds `export`). testQuoteExpiry.js fails on drift.
// qexp-6f2a9d38-mirror BEGIN
//
// LOCK — EXPIRED IS DERIVED, NEVER WRITTEN.
//   Nothing in this seam writes quote.status and nothing writes an Expired
//   pipeline stage. The state is computed from expiresAt on every read, exactly
//   as "Converted" is computed from convertedToJobId. A stored Expired would
//   have to be un-written the moment someone extends the date, and would desync
//   the instant an org changed its validity period.
//
// NO STAGE INPUTS, DELIBERATELY.
//   quotePipeline.js is INTERNAL ONLY — stage labels like "Lost" must never
//   reach a customer's browser. So this rule reads ONLY expiresAt, status and
//   convertedToJobId: the three things the customer portal already holds. That
//   is what lets the quote list, the detail page, the portal, the Seam 8 board
//   and Seam 12 reporting share ONE rule instead of five implementations.
//
// VALID *THROUGH* THE EXPIRY DATE.
//   daysLeft === 0 means "expires today, still live". Comparing instants
//   instead would flip an estimate to expired at local midnight while its own
//   PDF still reads "VALID UNTIL <today>" — which is what the portal did before
//   this seam. Days are counted as CALENDAR days in the org's timezone, the
//   same timezone the Seam 2 PDF and email blocks already format in.
export const EXPIRY_STATES = {
  NONE:          'none',           // expiry says nothing about this estimate
  ACTIVE:        'active',         // live, outside the warning window
  EXPIRING_SOON: 'expiring_soon',  // live, inside the warning window
  EXPIRED:       'expired',        // past its expiry date
};
export const EXPIRY_STATE_VALUES = Object.values(EXPIRY_STATES);

//   org doc: quoteDefaults { expiryWarningDays: number }
// How many days BEFORE expiry an estimate starts reading "Expiring soon".
// Default 4 — a heads-up before the fact is actionable; a notice after it is
// not. Capped at MAX_QUOTE_VALIDITY_DAYS: warning earlier than the longest
// possible validity period would mark an estimate expiring from birth.
export const DEFAULT_EXPIRY_WARNING_DAYS = 4;
export const MAX_EXPIRY_WARNING_DAYS     = MAX_QUOTE_VALIDITY_DAYS;
export const EXPIRY_WARNING_PRESETS      = [3, 4, 5, 7, 10];

// Matches the org timezone fallback used by the Seam 2 PDF/email blocks.
export const EXPIRY_FALLBACK_TIMEZONE = 'America/Toronto';

// Never throws: anything that isn't a whole number of days in 1..MAX resolves
// to DEFAULT_EXPIRY_WARNING_DAYS.
export function resolveExpiryWarningDays(quoteDefaults) {
  const n = Number(quoteDefaults?.expiryWarningDays);
  return Number.isInteger(n) && n >= 1 && n <= MAX_EXPIRY_WARNING_DAYS ? n : DEFAULT_EXPIRY_WARNING_DAYS;
}

// Firestore Timestamp | {seconds} | Date | ISO string | millis → Date, else
// null. Never throws. A quote saved before the field existed (25 of 142 in
// production) arrives here as undefined and must resolve to null, not "now".
export function toExpiryDate(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value.toDate === 'function') {
    try {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch (_err) { return null; }
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    const d = new Date(value.seconds * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

// A date's calendar day in a timezone, as 'YYYY-MM-DD'. en-CA formats in that
// order natively. A malformed timezone falls back rather than throwing — the
// same guard the Seam 2 PDF and email blocks use.
export function ymdInTimeZone(date, timeZone) {
  const opts = { year: 'numeric', month: '2-digit', day: '2-digit' };
  try {
    return new Intl.DateTimeFormat('en-CA', { ...opts, timeZone: timeZone || EXPIRY_FALLBACK_TIMEZONE }).format(date);
  } catch (_tzErr) {
    return new Intl.DateTimeFormat('en-CA', { ...opts, timeZone: EXPIRY_FALLBACK_TIMEZONE }).format(date);
  }
}

// Whole calendar days from one 'YYYY-MM-DD' to another. Both are anchored at
// UTC midnight, so DST transitions in the org's timezone can't produce a 23- or
// 25-hour day and round to the wrong integer.
export function calendarDaysBetween(fromYmd, toYmd) {
  const a = fromYmd.split('-').map(Number);
  const b = toYmd.split('-').map(Number);
  return Math.round((Date.UTC(b[0], b[1] - 1, b[2]) - Date.UTC(a[0], a[1] - 1, a[2])) / 86400000);
}

// THE expiry state of one estimate. Returns { state, daysLeft, expiresAt },
// where daysLeft is null unless a real expiry applies and expiresAt is a JS
// Date (callers format it themselves).
//
// opts: { now?, timeZone?, warningDays? } — all optional and all defended, so a
// surface that has none of them still gets a correct expired/active answer.
//
// Returns NONE unless the estimate is genuinely awaiting a customer decision:
//   - no expiresAt (legacy estimates predate the field; Seam 2 does not backfill)
//   - converted to a job (closed, like deriveSystemStageId's first rule)
//   - any status but Sent / ChangesRequested. A Draft is not in the customer's
//     hands; Approved / AwaitingDeposit / Declined are already decided. Without
//     this, 59 of the 77 past-expiry estimates in production would light up as
//     "Expired" for no reason anyone could act on.
export function resolveExpiryState(quote, opts) {
  const none = { state: EXPIRY_STATES.NONE, daysLeft: null, expiresAt: null };
  const q = quote || {};
  const o = opts || {};

  const expiresAt = toExpiryDate(q.expiresAt);
  if (!expiresAt) return none;
  if (q.convertedToJobId && q.source !== 'booking_page') return none; // bnpolish-7c3e9a14 — Book Now makes its Pending job (and so the marker) at once; its estimate still expires

  const status = normalizeStatus(q.status);
  if (status !== QUOTE_STATUSES.SENT && status !== QUOTE_STATUSES.CHANGES_REQUESTED) return none;

  const now = toExpiryDate(o.now) || new Date();
  const tz  = o.timeZone;
  const daysLeft = calendarDaysBetween(ymdInTimeZone(now, tz), ymdInTimeZone(expiresAt, tz));

  if (daysLeft < 0) return { state: EXPIRY_STATES.EXPIRED, daysLeft, expiresAt };

  const wn = Number(o.warningDays);
  const warningDays = Number.isInteger(wn) && wn >= 1 && wn <= MAX_EXPIRY_WARNING_DAYS
    ? wn
    : DEFAULT_EXPIRY_WARNING_DAYS;

  return {
    state: daysLeft <= warningDays ? EXPIRY_STATES.EXPIRING_SOON : EXPIRY_STATES.ACTIVE,
    daysLeft,
    expiresAt,
  };
}
// qexp-6f2a9d38-mirror END

// ────────────────────────────────────────────────────────────────────
// Quote activity log (Seam 3) — qact-3b7e9d52-schema
// ────────────────────────────────────────────────────────────────────
// Entry types for organizations/{orgId}/quotes/{quoteId}/activity/{id}.
// Entries are written ONLY by Cloud Functions; the web app just reads and
// labels them. Mirror of functions/utils/quoteSchema.js (mobile omits these —
// no timeline there yet). The full list is defined up front, including types
// no code writes yet, so the timeline must render any of them.
export const QUOTE_ACTIVITY_TYPES = {
  SENT:               'sent',
  STAGE_CHANGE:       'stage_change',
  FOLLOW_UP:          'follow_up',
  REMINDER_SENT:      'reminder_sent',
  VIEWED:             'viewed',
  SNOOZED:            'snoozed',
  UNSNOOZED:          'unsnoozed',
  EXPIRY_WARNED:      'expiry_warned',
  CLOSED:             'closed',
  NOTE_ADDED:         'note_added',
  CUSTOMER_RESPONDED: 'customer_responded',
  DELETED:            'deleted',   // estdel-f9ac8ba8 — writer: setQuoteArchived
  RESTORED:           'restored',  // estdel-f9ac8ba8 — writers: setQuoteArchived, onQuoteStatusChanged
};

export const QUOTE_ACTIVITY_TYPE_VALUES = Object.values(QUOTE_ACTIVITY_TYPES);

// ────────────────────────────────────────────────────────────────────
// Pipeline stages (Seam 4) — qpipe-4d1a7c35-schema
// ────────────────────────────────────────────────────────────────────
// VERBATIM mirror of the BEGIN..END block in functions/utils/quoteSchema.js
// (this copy only adds `export`). testQuotePipeline.js fails on drift.
// Web uses it to DISPLAY stages and outcomes; it never writes them.
// qpipe-4d1a7c35-mirror BEGIN
//
// Stage DEFINITIONS live in organizations/{orgId}/settings/pipeline — a
// staff-only, server-write-only document (never the org doc: the customer
// portal downloads the whole org doc). A quote carries only
// pipelineStageId + pipelineStageSetAt, both written ONLY by Cloud Functions
// through functions/utils/quotePipeline.js.
//
// The OUTCOME (open/won/lost/deferred) is NOT stored on the quote. It is
// always derived from the type of the stage the quote is in
// (resolveStageOutcome), so stage and outcome can never disagree and no
// outcome word ever reaches a customer's browser.
//
// Code refers to stages by ID only — never by label. The only ids code knows
// are the five system ids below, and only deriveSystemStageId uses them.
// Custom stages get random server-generated ids, so a custom stage is a
// first-class citizen for anything keyed on stage id or type.
export const PIPELINE_STAGE_TYPES = {
  OPEN:     'open',
  WON:      'won',
  LOST:     'lost',
  DEFERRED: 'deferred',
};
export const PIPELINE_STAGE_TYPE_VALUES = Object.values(PIPELINE_STAGE_TYPES);
export const PIPELINE_CLOSED_STAGE_TYPES = ['won', 'lost', 'deferred'];

export const PIPELINE_SYSTEM_STAGE_IDS = {
  DRAFT:             'draft',
  SENT:              'sent',
  VIEWED:            'viewed',            // reached ONLY by the Seam 10 view mover (qview-6b2d4e91), never by status
  CHANGES_REQUESTED: 'changes_requested',
  WON:               'won',
};
export const PIPELINE_SYSTEM_STAGE_ID_VALUES = Object.values(PIPELINE_SYSTEM_STAGE_IDS);

export const PIPELINE_MAX_STAGES      = 30;  // also keeps an `in` query on stage ids within Firestore's 30-value limit
export const PIPELINE_LABEL_MAX       = 40;
export const PIPELINE_STALE_DAYS_MAX  = 365;
export const PIPELINE_TEMPLATE_ID_PREFIX = 'tmpl_';

// The starter set every org begins with. FROZEN — never edit it; a future
// change becomes _V2. Custom rows carry tmpl_ placeholder ids that never land
// on a quote: the server swaps them for random per-org ids the first time the
// org's settings/pipeline document is materialized.
export const PIPELINE_STARTER_TEMPLATE_V1 = Object.freeze([
  { id: 'draft',             label: 'Draft',                    type: 'open',     staleAfterDays: null, isSystem: true  },
  { id: 'sent',              label: 'Sent',                     type: 'open',     staleAfterDays: 5,    isSystem: true  },
  { id: 'viewed',            label: 'Viewed',                   type: 'open',     staleAfterDays: 4,    isSystem: true  },
  { id: 'changes_requested', label: 'Changes requested',        type: 'open',     staleAfterDays: 3,    isSystem: true  },
  { id: 'tmpl_01',           label: 'In discussion',            type: 'open',     staleAfterDays: 5,    isSystem: false },
  { id: 'tmpl_02',           label: 'Waiting on client',        type: 'open',     staleAfterDays: 7,    isSystem: false },
  { id: 'tmpl_03',           label: 'Waiting on financing',     type: 'open',     staleAfterDays: 21,   isSystem: false },
  { id: 'tmpl_04',           label: 'Waiting on another trade', type: 'open',     staleAfterDays: 21,   isSystem: false },
  { id: 'tmpl_05',           label: 'Scope changing',           type: 'open',     staleAfterDays: 7,    isSystem: false },
  { id: 'tmpl_06',           label: 'Unable to reach',          type: 'open',     staleAfterDays: 10,   isSystem: false },
  { id: 'won',               label: 'Won',                      type: 'won',      staleAfterDays: null, isSystem: true  },
  { id: 'tmpl_07',           label: 'Lost',                     type: 'lost',     staleAfterDays: null, isSystem: false },
  { id: 'tmpl_08',           label: 'Deferred',                 type: 'deferred', staleAfterDays: null, isSystem: false },
].map((s, i) => Object.freeze({ ...s, order: (i + 1) * 10 })));

export function isPipelineSystemStageId(id) {
  return PIPELINE_SYSTEM_STAGE_ID_VALUES.includes(id);
}

// Stages from a settings/pipeline document (or the starter template when the
// document is missing or unusable), as fresh objects sorted by order. Display
// and derivation only — the server validates stage lists on write.
export function resolvePipelineStages(settingsDoc) {
  const raw = settingsDoc && Array.isArray(settingsDoc.stages) && settingsDoc.stages.length
    ? settingsDoc.stages
    : PIPELINE_STARTER_TEMPLATE_V1;
  return raw
    .filter((s) => s && typeof s.id === 'string' && s.id)
    .map((s) => ({
      id: s.id,
      label: typeof s.label === 'string' ? s.label : s.id,
      type: PIPELINE_STAGE_TYPE_VALUES.includes(s.type) ? s.type : 'open',
      order: Number.isFinite(s.order) ? s.order : 0,
      staleAfterDays: Number.isInteger(s.staleAfterDays) ? s.staleAfterDays : null,
      isSystem: isPipelineSystemStageId(s.id),
    }))
    .sort((a, b) => a.order - b.order);
}

export function findPipelineStage(stageId, stages) {
  if (!stageId || !Array.isArray(stages)) return null;
  return stages.find((s) => s.id === stageId) || null;
}

// THE outcome of a quote: the type of the stage it is in. null when the quote
// has no stage or its stage id is unknown.
export function resolveStageOutcome(stageId, stages) {
  const stage = findPipelineStage(stageId, stages);
  return stage ? stage.type : null;
}

export function isClosedStageType(type) {
  return PIPELINE_CLOSED_STAGE_TYPES.includes(type);
}

// Status (and conversion) → the stage the quote belongs in automatically.
// Reads status; never writes anything. First match wins:
//   converted to a job (Marcus 2026-09-14)  → won
//   Approved / Accepted                     → won
//   AwaitingDeposit (Marcus 2026-09-14)     → won
//   Declined                                → lowest-order stage of type lost
//   ChangesRequested                        → changes_requested
//   Sent                                    → sent
//   Draft                                   → draft
//   Expired                                 → sent, ONLY if the quote has no stage (Seam 5 owns expiry)
//   missing / unknown status                → draft, ONLY if the quote has no stage
// Returns { target, onlyIfUnstaged, rule }. target null = no automatic stage.
export function deriveSystemStageId(quote, stages) {
  const S = PIPELINE_SYSTEM_STAGE_IDS;
  const q = quote || {};
  if (q.convertedToJobId) return { target: S.WON, onlyIfUnstaged: false, rule: 'converted' };
  const status = normalizeStatus(q.status);
  // normalizeStatus already folds 'Accepted' and lowercase variants in.
  if (status === QUOTE_STATUSES.APPROVED) return { target: S.WON, onlyIfUnstaged: false, rule: 'approved' };
  if (status === QUOTE_STATUSES.AWAITING_DEPOSIT) return { target: S.WON, onlyIfUnstaged: false, rule: 'awaiting_deposit' };
  if (status === QUOTE_STATUSES.DECLINED) {
    const lost = (Array.isArray(stages) ? stages : [])
      .filter((s) => s && s.type === 'lost')
      .sort((a, b) => a.order - b.order)[0];
    return lost
      ? { target: lost.id, onlyIfUnstaged: false, rule: 'declined' }
      : { target: null, onlyIfUnstaged: false, rule: 'declined_no_lost_stage' };
  }
  if (status === QUOTE_STATUSES.CHANGES_REQUESTED) return { target: S.CHANGES_REQUESTED, onlyIfUnstaged: false, rule: 'changes_requested' };
  if (status === QUOTE_STATUSES.SENT)    return { target: S.SENT,  onlyIfUnstaged: false, rule: 'sent' };
  if (status === QUOTE_STATUSES.DRAFT)   return { target: S.DRAFT, onlyIfUnstaged: false, rule: 'draft' };
  if (status === QUOTE_STATUSES.EXPIRED) return { target: S.SENT,  onlyIfUnstaged: true,  rule: 'expired' };
  return { target: S.DRAFT, onlyIfUnstaged: true, rule: 'unknown_status' };
}
// qpipe-4d1a7c35-mirror END

// ────────────────────────────────────────────────────────────────────
// Follow-up vocabularies (Seam 6) — qfup-7d3e9a41-schema
// ────────────────────────────────────────────────────────────────────
// VERBATIM mirror of the BEGIN..END block in functions/utils/quoteSchema.js
// (this copy only adds `export`). functions/scripts/testQuoteFollowUp.js
// fails if the two drift. Mobile deliberately has no copy.
// qfup-7d3e9a41-mirror BEGIN
//
// Stacey Boychuk's Follow-Up Method and Result vocabularies, adopted VERBATIM
// from the seventh dropdown in her spreadsheet. The three axes in the result
// list overlap (what happened / who acts next / which channel) — that is hers
// to resolve by picking sensibly, not ours to redesign. Results are ordered
// outcomes-first so the sensible pick is also the default pick.
//
// DEFINITIONS live in organizations/{orgId}/settings/quoteVocabularies — a
// SIBLING of settings/pipeline, not keys on it: that document is written with
// a wholesale tx.set, so sibling keys would be silently deleted by the next
// stage save. Seam 9 (qclose-3e8a5c17) added closeReasons /
// priceFeedbackOptions to this same document rather than creating a third.
//
// A stored activity entry records the WORDS, not a code: an audit row must say
// what was true when it was written, and must not fail revalidation after an
// org renames a value. So the server validates SHAPE (isVocabValue) and
// membership lives in the picker.
export const FOLLOW_UP_METHOD_DEFAULTS = [
  'Phone',
  'Email',
  'Text',
  'Voicemail',
  'In person',
  'No contact made',
];
export const FOLLOW_UP_RESULT_DEFAULTS = [
  'Spoke with client',
  'No response',
  'Left voicemail',
  'Client will contact us',
  'Follow-up scheduled',
  'Email sent',
  'Text sent',
];

// qclose-3e8a5c17 — Seam 9 close capture. Stacey's Decision Factor (12) and
// Price Feedback (6) dropdowns, adopted VERBATIM as starters (founder
// decision 2026-09-30, the Seam 6 precedent), editable per org. Asked on wins
// as well as losses: "Existing relationship" and "Price was acceptable" on a
// win are what make the loss numbers mean anything.
export const CLOSE_REASON_DEFAULTS = [
  'Price',
  'Availability/timing',
  'Existing relationship',
  'Different proposed solution',
  'Financing/payment options',
  'Communication/responsiveness',
  'Reputation/referral',
  'Scope changed',
  'Project cancelled',
  'Not urgent',
  "Unknown/client didn't say",
  'Other',
];
export const PRICE_FEEDBACK_DEFAULTS = [
  'Our price was slightly higher',
  'Our price was significantly higher',
  'Budget was lower than expected',
  'Price was acceptable',
  'Not enough information to know',
  'Not applicable',
];

export const VOCAB_MAX_LENGTH = 60;
export const VOCAB_MAX_ITEMS  = 30;   // matches PIPELINE_MAX_STAGES

// A usable vocabulary value: a string that is still non-empty once trimmed and
// fits the cap. This is the ONLY membership rule the server applies to a
// logged follow-up.
export function isVocabValue(v) {
  if (typeof v !== 'string') return false;
  const t = v.trim();
  return t.length > 0 && t.length <= VOCAB_MAX_LENGTH;
}

// Normalize one stored list: drop non-strings, trim, drop empty and over-long
// values, de-duplicate case-insensitively (first spelling wins), cap at
// VOCAB_MAX_ITEMS. Falls back to `defaults` when the list is absent or
// resolves empty — an org with no methods could not log at all.
export function resolveVocabList(raw, defaults) {
  const source = Array.isArray(raw) ? raw : [];
  const out = [];
  const seen = new Set();
  for (const item of source) {
    if (!isVocabValue(item)) continue;
    const t = item.trim();
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
    if (out.length >= VOCAB_MAX_ITEMS) break;
  }
  return out.length ? out : defaults.slice();
}

// NEVER THROWS — deliberately total. An absent, malformed or hostile settings
// document must degrade to the frozen defaults, not break the Log Follow-up
// modal. The try/catch makes that a guarantee rather than an intention.
export function resolveQuoteVocabularies(settingsDoc) {
  try {
    const doc = (settingsDoc && typeof settingsDoc === 'object' && !Array.isArray(settingsDoc))
      ? settingsDoc
      : {};
    return {
      followUpMethods: resolveVocabList(doc.followUpMethods, FOLLOW_UP_METHOD_DEFAULTS),
      followUpResults: resolveVocabList(doc.followUpResults, FOLLOW_UP_RESULT_DEFAULTS),
      closeReasons: resolveVocabList(doc.closeReasons, CLOSE_REASON_DEFAULTS),
      priceFeedbackOptions: resolveVocabList(doc.priceFeedbackOptions, PRICE_FEEDBACK_DEFAULTS),
    };
  } catch (e) {
    return {
      followUpMethods: FOLLOW_UP_METHOD_DEFAULTS.slice(),
      followUpResults: FOLLOW_UP_RESULT_DEFAULTS.slice(),
      closeReasons: CLOSE_REASON_DEFAULTS.slice(),
      priceFeedbackOptions: PRICE_FEEDBACK_DEFAULTS.slice(),
    };
  }
}
// qfup-7d3e9a41-mirror END

// ────────────────────────────────────────────────────────────────────
// Attention & staleness (Seam 8) — qattn-8c4e1f92-schema
// ────────────────────────────────────────────────────────────────────
// Everything between BEGIN and END is mirrored VERBATIM in
// functions/utils/quoteSchema.js (this web copy only adds `export`);
// functions/scripts/testQuoteAttention.js fails if the two drift.
// Mobile deliberately has no copy: it carries the expiry block only and has
// no pipeline mirror at all, so it cannot evaluate a stage-keyed rung.
// qattn-8c4e1f92-mirror BEGIN
//
// WHAT THIS IS. The consumer for staleAfterDays, which has been stored,
// validated and editable since Seam 4 and read by nothing. One pure rule,
// shared by the estimates list and the estimate page so they can never
// disagree, shaped like scheduleHealth in src/utils/recurringInvoices.js.
//
// DISPLAY ONLY. Nothing here sends an email, a push or a notification, and
// nothing here is ever written to a document. Every input is already on the
// quote, so every surface gets this for free — no extra read, no new field,
// no trigger, no rules change.
//
// EXPIRY IS NOT A RUNG, DELIBERATELY.
//   Expiring-soon and Expired are already the STATUS pill (Seam 5, rendered
//   by QuoteStatusBadge). Repeating them on the attention axis would put the
//   same fact on the same row twice. The two axes stay orthogonal, exactly as
//   needsQbAttention sits orthogonal to invoice status. Measured 2026-09-29:
//   treating past-expiry as a rung lit up 42 of 55 open estimates, because
//   most of them are Drafts and closed estimates that resolveExpiryState
//   already silences for precisely this reason.
//
// SNOOZE SUPPRESSES EVERYTHING, IN ONE PLACE.
//   The check is at the top and returns early, so a new rung added below is
//   suppressed by construction rather than by remembering to guard it.
export const QUOTE_ATTENTION_RUNGS = {
  FOLLOW_UP_OVERDUE: 'followUpOverdue',
  AWAITING_DEPOSIT:  'awaitingDeposit',
  STALE:             'stale',
  NO_NEXT_STEP:      'noNextStep',
};
// Precedence, most urgent first. `primary` is the one the list chip names;
// `reasons` carries them all for the estimate page, which is why the very
// common stale + noNextStep pair does not lose its actionable half.
export const QUOTE_ATTENTION_RUNG_ORDER = [
  QUOTE_ATTENTION_RUNGS.FOLLOW_UP_OVERDUE,
  QUOTE_ATTENTION_RUNGS.AWAITING_DEPOSIT,
  QUOTE_ATTENTION_RUNGS.STALE,
  QUOTE_ATTENTION_RUNGS.NO_NEXT_STEP,
];

// A deposit that is still owed. recorded/paid/waived/refunded are all settled
// as far as chasing goes — refunded means somebody already dealt with it.
export const QUOTE_ATTENTION_DEPOSIT_OWED = ['unpaid'];

// Pure. How many days late is this estimate's follow-up, or null if it is not
// late (no date, still future, or snoozed).
//
// THE ONE OVERDUE PREDICATE. Before Seam 8 there were two hand-rolled copies —
// one on the estimate page and one on the calendar — and both compared against
// the VIEWER's midnight, so a salesperson in Vancouver looking at a Toronto
// business saw a follow-up turn amber three hours late. Both now call this.
// Counted in CALENDAR days in the org timezone, the same way expiry is: a
// date-only follow-up is a day, not an instant.
export function quoteFollowUpOverdueDays(quote, opts) {
  const q = quote || {};
  const o = opts || {};
  const nextAt = toExpiryDate(q.nextFollowUpAt);
  if (!nextAt) return null;

  const now = toExpiryDate(o.now) || new Date();
  const snoozedUntil = toExpiryDate(q.snoozedUntil);
  // A snoozed estimate is not overdue — the suppression is the whole point.
  if (snoozedUntil && snoozedUntil.getTime() > now.getTime()) return null;

  const tz = o.timeZone || EXPIRY_FALLBACK_TIMEZONE;
  const d = calendarDaysBetween(ymdInTimeZone(now, tz), ymdInTimeZone(nextAt, tz));
  return d < 0 ? -d : null;
}

// Pure. When did this estimate last show a sign of life?
//
// THE LATER OF stage entry AND last human touch. Taking the later of the two
// is load-bearing and it dissolves an open TODO rather than working around it:
// setQuotePipelineStage deliberately does NOT stamp lastActivityAt (the field
// list is frozen and assertStageUpdateShape throws on an extra key), so a
// touch-only clock would ignore a card being moved; and lastActivityAt is not
// stamped by any sweep, so a stage-only clock would ignore an edit or a logged
// follow-up. The later of the two catches both WITHOUT unfreezing that lock.
//
// Returns null when neither field is present, which means "no clock" — never
// "stale since the epoch".
export function resolveQuoteQuietSince(quote) {
  const q = quote || {};
  const stageAt = toExpiryDate(q.pipelineStageSetAt);
  const touched = toExpiryDate(q.lastActivityAt);
  if (!stageAt && !touched) return null;
  if (!stageAt) return touched;
  if (!touched) return stageAt;
  return stageAt.getTime() >= touched.getTime() ? stageAt : touched;
}

/**
 * Pure. What needs a human's attention on this estimate, and why.
 *
 * @param quote  the quote document
 * @param stage  the resolved pipeline stage it sits in (findPipelineStage), or
 *               null/undefined when the stage list has not loaded yet — in
 *               which case the two stage-keyed rungs stay silent rather than
 *               guessing, and the surface shows nothing rather than something
 *               wrong.
 * @param opts   { now, timeZone } — timeZone is the ORG timezone. 5 of the 11
 *               orgs holding estimates have no timezone field, so the fallback
 *               is what stands between them and the viewer's own midnight.
 *
 * @returns { level, primary, rungs, reasons, quietDays, snoozed }
 */
export function resolveQuoteAttention(quote, stage, opts) {
  const q = quote || {};
  const o = opts || {};
  const none = (snoozed) => ({
    level: 'none', primary: null, rungs: [], reasons: [], quietDays: null, snoozed: !!snoozed,
  });

  // Closed is closed. Converted, Approved and Declined are decided; nobody
  // needs chasing. AwaitingDeposit is deliberately NOT closed — that is the
  // whole point of its rung.
  if (q.convertedToJobId) return none(false);
  const status = normalizeStatus(q.status);
  if (status === QUOTE_STATUSES.APPROVED || status === QUOTE_STATUSES.DECLINED) return none(false);

  const now = toExpiryDate(o.now) || new Date();

  // Snoozed: silent, whatever else is true. DERIVED from snoozedUntil vs now
  // (roadmap LOCK 3) — no stored flag, so an elapsed snooze is awake the
  // instant its date passes whether or not the nightly sweep has run.
  const snoozedUntil = toExpiryDate(q.snoozedUntil);
  if (snoozedUntil && snoozedUntil.getTime() > now.getTime()) return none(true);

  const tz = o.timeZone || EXPIRY_FALLBACK_TIMEZONE;
  const nowYmd = ymdInTimeZone(now, tz);

  const rungs = [];
  const reasons = [];

  // 1. FOLLOW-UP OVERDUE — through the shared predicate, so the estimate page,
  //    the calendar and this rule can never disagree about one date.
  const nextAt = toExpiryDate(q.nextFollowUpAt);
  // qclose-3e8a5c17 — NOT for an estimate in a LOST or DEFERRED stage. The
  // status check above only catches Approved/Declined/converted; an estimate
  // still marked Sent that someone moved into a Lost or Deferred stage has
  // been decided, and its old follow-up date is not a chase any more.
  // WON is deliberately NOT included: AwaitingDeposit always sits in Won, and
  // an overdue follow-up there is a real chase — for the deposit (pinned by
  // "overdue follow-up outranks an owed deposit" in testQuoteAttention.js).
  const inDeadStage = !!stage
    && (stage.type === PIPELINE_STAGE_TYPES.LOST || stage.type === PIPELINE_STAGE_TYPES.DEFERRED);
  const overdueDays = inDeadStage ? null : quoteFollowUpOverdueDays(q, { now, timeZone: tz });
  if (overdueDays !== null) {
    rungs.push(QUOTE_ATTENTION_RUNGS.FOLLOW_UP_OVERDUE);
    reasons.push(overdueDays === 1
      ? 'Follow-up was due yesterday.'
      : `Follow-up was due ${overdueDays} days ago.`);
  }

  // 2. AWAITING DEPOSIT. The customer said yes and the money has not landed.
  //    depositStatus may be absent on an older estimate; absent means unpaid
  //    here, because the status alone already says a deposit is being waited on.
  if (status === QUOTE_STATUSES.AWAITING_DEPOSIT) {
    const ds = q.depositStatus;
    if (!ds || QUOTE_ATTENTION_DEPOSIT_OWED.includes(ds)) {
      rungs.push(QUOTE_ATTENTION_RUNGS.AWAITING_DEPOSIT);
      reasons.push('Approved, but the deposit has not been paid yet.');
    }
  }

  // 3. STALE — per stage, never global. The threshold is the stage's own
  //    staleAfterDays; null means this stage has no clock, which is how every
  //    closed stage and Draft are exempt (validation refuses a value on them).
  const quietSince = resolveQuoteQuietSince(q);
  const quietDays = quietSince ? calendarDaysBetween(ymdInTimeZone(quietSince, tz), nowYmd) : null;
  const threshold = stage && Number.isInteger(stage.staleAfterDays) ? stage.staleAfterDays : null;
  if (threshold !== null && quietDays !== null && quietDays > threshold) {
    rungs.push(QUOTE_ATTENTION_RUNGS.STALE);
    const where = stage.label ? ` in ${stage.label}` : '';
    reasons.push(`Gone quiet — ${quietDays} days${where} with no activity.`);
  }

  // 4. NO NEXT STEP. Only for an estimate that is actually in play: an OPEN
  //    stage, and not a Draft. A draft is not in the customer's hands, so
  //    there is nothing to chase and nagging about it would be noise.
  if (!nextAt
      && stage && stage.type === PIPELINE_STAGE_TYPES.OPEN
      && status !== QUOTE_STATUSES.DRAFT) {
    rungs.push(QUOTE_ATTENTION_RUNGS.NO_NEXT_STEP);
    reasons.push('No next step set.');
  }

  if (!rungs.length) {
    return { level: 'none', primary: null, rungs: [], reasons: [], quietDays, snoozed: false };
  }
  const primary = QUOTE_ATTENTION_RUNG_ORDER.find((r) => rungs.includes(r)) || rungs[0];
  return { level: 'attention', primary, rungs, reasons, quietDays, snoozed: false };
}

// The short label the list chip shows — the primary rung only. Kept beside the
// rule so a new rung cannot ship with no word for it.
export const QUOTE_ATTENTION_LABELS = {
  followUpOverdue: 'Follow-up due',
  awaitingDeposit: 'Deposit owed',
  stale:           'Gone quiet',
  noNextStep:      'No next step',
};

export function quoteAttentionLabel(attention) {
  const p = attention && attention.primary;
  return p ? (QUOTE_ATTENTION_LABELS[p] || 'Needs attention') : null;
}

// Filter predicate — its own axis, mirrors needsAttention in
// src/utils/recurringInvoices.js and needsQbAttention in InvoicesView.
export function quoteNeedsAttention(quote, stage, opts) {
  return resolveQuoteAttention(quote, stage, opts).level === 'attention';
}
// qattn-8c4e1f92-mirror END

// ────────────────────────────────────────────────────────────────────
// Deleted (hidden) estimates — estdel-f9ac8ba8
// ────────────────────────────────────────────────────────────────────
// VERBATIM mirror of the BEGIN..END block in functions/utils/quoteSchema.js
// (this copy only adds `export`). testEstimateDelete.js fails on drift. The
// web uses it to decide what the screens offer; the server is the real gate.
// estdel-f9ac8ba8-mirror BEGIN
//
// "Delete" HIDES an estimate; nothing is ever erased (CRA six-year rule). The
// quote carries archived: true + archivedAt + archivedBy {uid, name, role} —
// the catalog's archive convention — and Restore sets archived: false +
// restoredAt + restoredBy. Only the setQuoteArchived Cloud Function writes
// these fields: firestore.rules refuses them from the app, and refuses every
// app edit of an estimate while it is deleted.
//
// A MISSING field means NOT deleted — no estimate written before this seam has
// one. So readers filter in code (isQuoteArchived) and never with a Firestore
// where(), which would match nothing on a document without the field.
export const QUOTE_DELETABLE_STATUSES = ['Draft', 'Sent', 'ChangesRequested', 'Declined', 'Expired'];
// Owner / Admin / Manager (every spelling in production): any deletable estimate.
export const QUOTE_DELETE_ANY_ROLES = ['Admin', 'Manager', 'Owner/System Admin', 'Owner / System Admin', 'Owner', 'System Admin'];
// Sales Consultant: only a Draft credited to them (resolveQuoteSalesPerson —
// the Estimates report's definition). Founder decision 2026-10-29.
export const QUOTE_DELETE_OWN_DRAFT_ROLE = 'Sales Consultant';
// Restore: Owner and Admin only. Founder decision 2026-10-29.
export const QUOTE_RESTORE_ROLES = ['Admin', 'Owner/System Admin', 'Owner / System Admin', 'Owner', 'System Admin'];

export function isQuoteArchived(quote) {
  return !!quote && quote.archived === true;
}

// Why THIS estimate cannot be deleted, whoever asks; null = it can be.
//   'deleted'   already deleted
//   'status'    Approved / Accepted / Awaiting Deposit / any status not listed above
//   'approved'  the customer signed it at some point (approvedAt / an approval record)
//   'deposit'   a deposit was ever started — any depositStatus (unpaid = pending), or a
//               Stripe session / payment / paid / recorded / applied mark
//   'converted' made into a job
// A job or invoice pointing back at it (their quoteId) is checked by the server only.
export function quoteDeleteBlocker(quote) {
  if (!quote) return 'missing';
  if (isQuoteArchived(quote)) return 'deleted';
  if (!QUOTE_DELETABLE_STATUSES.includes(normalizeStatus(quote.status))) return 'status';
  if (quote.approvedAt || quote.latestApprovalId || quote.selectionsLocked === true) return 'approved';
  const dep = quote.deposit || {};
  if (quote.depositStatus || dep.stripeSessionId || dep.stripePaymentIntentId || dep.paidAt
      || dep.recordedAt || dep.appliedToInvoiceId) return 'deposit';
  if (quote.convertedToJobId) return 'converted';
  return null;
}

// May this person delete this estimate? role = their users-doc role, uid = their id.
export function canDeleteQuote(quote, role, uid) {
  if (!role || quoteDeleteBlocker(quote) !== null) return false;
  if (QUOTE_DELETE_ANY_ROLES.includes(role)) return true;
  return role === QUOTE_DELETE_OWN_DRAFT_ROLE
    && normalizeStatus(quote.status) === QUOTE_STATUSES.DRAFT
    && !!uid && resolveQuoteSalesPerson(quote) === uid;
}

export function canRestoreQuote(quote, role) {
  return !!role && QUOTE_RESTORE_ROLES.includes(role) && isQuoteArchived(quote);
}

// The customer portal's words for a deleted estimate, and every portal refusal on one.
export function deletedQuotePortalMessage(quote) {
  return `This ${resolveDocTerminology(quote && quote.docType).word.toLowerCase()} is no longer available.`;
}
// Staff refusal when something tries to act on a deleted estimate.
export function deletedQuoteStaffMessage(quote) {
  return `This ${resolveDocTerminology(quote && quote.docType).word.toLowerCase()} was deleted. An Owner or Admin can restore it first.`;
}
// estdel-f9ac8ba8-mirror END
